import React, { useCallback } from 'react'

import {
  arrayOf, bool, number, shape, string,
} from 'prop-types'

import usePageView from 'Pods/Shared/Hooks/usePageView'
import I18n from 'Pods/Shared/I18n'

import SubscriptionTerms from '../Components/SubscriptionTerms/SubscriptionTerms'
import useCheckoutTracking from '../Hooks/useCheckoutTracking'
import OrderSummary from './Components/OrderSummary/OrderSummary'
import PaymentMethod from './Components/PaymentMethod/PaymentMethod'
import { toUpgradePlan } from './Constants/upgradePlans'
import useUpgradeCheckout from './Hooks/useUpgradeCheckout'
import useUpgradeQuote from './Hooks/useUpgradeQuote'
import { quoteFields } from './propTypes'

const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'
const DEFAULT_COMMIT_URL = '/profile/subscription/upgrade'

// The restructure quote replaces the legacy amounts, so they default to 0 when the BE omits them.
function Upgrade({
  premiumPrice = 0,
  downloadsUsed = 0,
  unusedCredits = 0,
  discount = 0,
  amountDueNow = 0,
  vatAmount = null,
  premiumPlan = null,
  paymentMethod = null,
  updatePaymentData = null,
  subscriptionTier = 'essentials',
  returnPath = DEFAULT_RETURN_PATH,
  commitUrl = DEFAULT_COMMIT_URL,
  plansRestructureEnabled = false,
  locale = 'en',
  ...quoteProps
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const serverQuote = quoteProps.targetPlan ? { ...quoteProps, vatAmount } : null
  const { quote, isQuoteLoading, changeTarget } = useUpgradeQuote({
    isEnabled: plansRestructureEnabled,
    serverQuote,
    quoteUrl: commitUrl,
    translate,
  })
  // The checkout re-picks the selected term to retry an intent update; here it would only refetch the same quote.
  const handleTargetTermChange = (term) => {
    const target = toUpgradePlan(quote.targetPlan.tier, term)
    if (target !== quote.target) changeTarget(target)
  }

  // The BE re-prices every commit; after a decline or a price change the user confirms a fresh quote.
  const handleQuoteStale = useCallback(() => {
    if (quote) changeTarget(quote.target)
  }, [quote, changeTarget])

  const netAmountDueNow = quote
    ? +(quote.amountDue - (quote.vatAmount || 0)).toFixed(2)
    : +(amountDueNow - (vatAmount || 0)).toFixed(2)

  const tracking = useCheckoutTracking({
    isUpgradeMode: true,
    tier: subscriptionTier,
    upgradeTier: quote?.targetPlan.tier,
    value: netAmountDueNow,
  })

  usePageView(tracking.trackView)

  const {
    useSavedMethod,
    isDifferentOpen,
    isProcessing,
    isPayDisabled,
    handleSelectSaved,
    handleToggleDifferent,
    handleFormReady,
    handleFormValidityChange,
    handlePaymentProcessing,
    handlePaymentSuccess,
    handlePay,
  } = useUpgradeCheckout({
    paymentMethod, commitUrl, returnPath, tracking, quote, onQuoteStale: handleQuoteStale, translate,
  })

  return (
    <div className="subscription-checkout subscription-upgrade">
      <div className="subscription-checkout__main">
        <div className="subscription-checkout__content">
          {isProcessing ? (
            <p className="upgrade-processing cgt-body-base">
              {translate('subscription_checkout.payment_success_processing')}
            </p>
          ) : (
            <PaymentMethod
              paymentMethod={paymentMethod}
              updatePaymentData={updatePaymentData}
              useSavedMethod={useSavedMethod}
              isDifferentOpen={isDifferentOpen}
              isPaymentUpdating={isQuoteLoading}
              onSelectSaved={handleSelectSaved}
              onToggleDifferent={handleToggleDifferent}
              onFormReady={handleFormReady}
              onFormValidityChange={handleFormValidityChange}
              onPaymentProcessing={handlePaymentProcessing}
              onPaymentSuccess={handlePaymentSuccess}
              tracking={tracking}
              locale={locale}
            />
          )}
        </div>
      </div>

      <div className="subscription-checkout__order-details">
        <OrderSummary
          premiumPrice={premiumPrice}
          discount={discount}
          downloadsUsed={downloadsUsed}
          unusedCredits={unusedCredits}
          amountDueNow={amountDueNow}
          vatAmount={vatAmount}
          premiumPlan={premiumPlan}
          quote={quote}
          isQuoteLoading={isQuoteLoading}
          onTargetTermChange={handleTargetTermChange}
          onPay={handlePay}
          disabled={isPayDisabled}
          locale={locale}
        />
        <SubscriptionTerms
          price={quote ? quote.targetPlan.price : premiumPrice}
          locale={locale}
          planKey={quote ? quote.targetPlan.planType : 'premium'}
          isPlansRestructure={Boolean(quote)}
        />
      </div>
    </div>
  )
}

Upgrade.propTypes = {
  premiumPrice: number,
  downloadsUsed: number,
  unusedCredits: number,
  discount: number,
  amountDueNow: number,
  vatAmount: number,
  premiumPlan: shape({
    features: arrayOf(string),
    originalPrice: number,
    currentPrice: number,
    priceUnit: string,
  }),
  paymentMethod: string,
  updatePaymentData: shape({
    merchant: string,
    signature: string,
    paymentIntent: string,
    returnPath: string,
  }),
  subscriptionTier: string,
  returnPath: string,
  commitUrl: string,
  plansRestructureEnabled: bool,
  locale: string,
  ...quoteFields,
}

export default Upgrade
